import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit, Plus, Trash2 } from 'lucide-react';

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { useEmailSignatures } from '@/hooks/useEmailSignatures';
import { EMAIL_TEMPLATES_QUERY_KEY, useEmailTemplates, type EmailTemplate } from '@/hooks/useEmailTemplates';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface EmailTemplateManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TemplateEditorState {
  name: string;
  category: string;
  subject: string;
  bodyHtml: string;
  defaultSignatureId: number | null;
  trackOpens: boolean;
  separateUntilReply: boolean;
}

const emptyEditor: TemplateEditorState = {
  name: '',
  category: 'Generale',
  subject: '',
  bodyHtml: '',
  defaultSignatureId: null,
  trackOpens: true,
  separateUntilReply: true,
};

function editorFromTemplate(template: EmailTemplate): TemplateEditorState {
  return {
    name: template.name,
    category: template.category,
    subject: template.subject,
    bodyHtml: template.bodyHtml,
    defaultSignatureId: template.defaultSignatureId ?? null,
    trackOpens: template.trackOpens,
    separateUntilReply: template.separateUntilReply,
  };
}

export default function EmailTemplateManagerDialog({ open, onOpenChange }: EmailTemplateManagerDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const templatesQuery = useEmailTemplates(open);
  const { signatures } = useEmailSignatures();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editor, setEditor] = useState<TemplateEditorState>(emptyEditor);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditorOpen(false);
      setEditingId(null);
      setEditor(emptyEditor);
    }
  }, [open]);

  const updateEditor = <K extends keyof TemplateEditorState>(key: K, value: TemplateEditorState[K]) => {
    setEditor((current) => ({ ...current, [key]: value }));
  };

  const openNew = () => {
    setEditingId(null);
    setEditor(emptyEditor);
    setEditorOpen(true);
  };

  const openEdit = (template: EmailTemplate) => {
    setEditingId(template.id);
    setEditor(editorFromTemplate(template));
    setEditorOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { ...editor, bodyText: null };
      if (editingId) return apiRequest('PUT', `/api/email/templates/${editingId}`, payload);
      return apiRequest('POST', '/api/email/templates', payload);
    },
    onSuccess: async (saved: EmailTemplate) => {
      await queryClient.invalidateQueries({ queryKey: EMAIL_TEMPLATES_QUERY_KEY });
      setEditingId(saved.id);
      setEditor(editorFromTemplate(saved));
      toast({ title: editingId ? 'Modello aggiornato' : 'Modello creato', description: 'Il modello è disponibile nel composer Email.' });
    },
    onError: (error) => {
      toast({ title: 'Salvataggio modello non riuscito', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: number) => apiRequest('DELETE', `/api/email/templates/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EMAIL_TEMPLATES_QUERY_KEY });
      setEditorOpen(false);
      setEditingId(null);
      setEditor(emptyEditor);
      toast({ title: 'Modello archiviato', description: 'Le email già inviate conservano il loro snapshot storico.' });
    },
    onError: (error) => {
      toast({ title: 'Archiviazione non riuscita', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    },
  });

  const templates = templatesQuery.data ?? [];
  const canSave = Boolean(editor.name.trim() && editor.category.trim() && editor.subject.trim() && editor.bodyHtml.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-email-template-manager="true" className="flex h-[min(92dvh,840px)] w-[calc(100vw-1rem)] max-w-6xl flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-border px-4 py-3 text-left sm:px-5">
          <DialogTitle className="text-base font-semibold">Modelli Email</DialogTitle>
          <DialogDescription className="text-xs font-normal leading-relaxed">
            Modelli riutilizzabili per invii manuali. Nessuna campagna o invio automatico.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-rows-[minmax(9rem,0.8fr)_minmax(0,1.2fr)] lg:grid-cols-[19rem_minmax(0,1fr)] lg:grid-rows-1">
          <aside className="flex min-h-0 flex-col border-b border-border lg:border-b-0 lg:border-r">
            <div className="shrink-0 p-3">
              <Button type="button" className="min-h-10 w-full rounded-none" onClick={openNew}>
                <Plus className="mr-2 h-4 w-4" />Nuovo modello
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto border-t border-border">
              {templatesQuery.isLoading && <p className="p-4 text-xs text-muted-foreground" role="status">Caricamento modelli…</p>}
              {templatesQuery.isError && <p className="p-4 text-xs text-destructive" role="alert">Non riesco a leggere i modelli.</p>}
              {templatesQuery.isSuccess && templates.length === 0 && <p className="p-4 text-xs leading-relaxed text-muted-foreground">Nessun modello salvato. Creane uno per riutilizzarlo nel composer.</p>}
              {templates.map((template) => (
                <div key={template.id} className="flex items-stretch border-b border-border last:border-b-0">
                  <button
                    type="button"
                    aria-label={`Modifica modello ${template.name}`}
                    onClick={() => openEdit(template)}
                    className={`min-w-0 flex-1 px-4 py-3 text-left hover:bg-muted ${editingId === template.id ? 'bg-primary/10' : ''}`}
                  >
                    <span className="block truncate text-sm font-medium">{template.name}</span>
                    <span className="mt-1 block truncate text-xs font-normal text-muted-foreground">{template.category} · {template.subject}</span>
                  </button>
                  <button type="button" aria-label={`Apri modello ${template.name}`} onClick={() => openEdit(template)} className="w-10 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground">
                    <Edit className="mx-auto h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </aside>

          <section className="min-h-0 overflow-y-auto p-4 sm:p-5" aria-label="Editor modello Email">
            {!editorOpen ? (
              <div className="flex h-full min-h-40 items-center justify-center border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                Seleziona un modello oppure creane uno nuovo.
              </div>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (canSave && !saveMutation.isPending) saveMutation.mutate();
                }}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-xs font-medium">Nome modello
                    <Input aria-label="Nome modello" className="mt-1 rounded-none" value={editor.name} onChange={(event) => updateEditor('name', event.target.value)} maxLength={160} required />
                  </label>
                  <label className="text-xs font-medium">Categoria
                    <Input aria-label="Categoria modello" className="mt-1 rounded-none" value={editor.category} onChange={(event) => updateEditor('category', event.target.value)} maxLength={80} required />
                  </label>
                </div>
                <label className="block text-xs font-medium">Oggetto
                  <Input aria-label="Oggetto modello" className="mt-1 rounded-none" value={editor.subject} onChange={(event) => updateEditor('subject', event.target.value)} maxLength={998} required />
                </label>
                <label className="block text-xs font-medium">Firma predefinita
                  <select aria-label="Firma predefinita del modello" className="mt-1 h-10 w-full border border-border bg-background px-3 text-sm" value={editor.defaultSignatureId ?? ''} onChange={(event) => updateEditor('defaultSignatureId', event.target.value ? Number(event.target.value) : null)}>
                    <option value="">Usa la firma del composer</option>
                    {(signatures ?? []).map((signature) => <option key={signature.id} value={signature.id}>{signature.name}</option>)}
                  </select>
                </label>
                <div>
                  <span className="mb-1 block text-xs font-medium">Corpo del modello</span>
                  <RichTextEditor value={editor.bodyHtml} onChange={(value) => updateEditor('bodyHtml', value)} className="min-h-[180px] border rounded-none bg-background p-3 text-xs" placeholder="Scrivi il contenuto riutilizzabile…" />
                </div>
                <div className="space-y-2 border border-border p-3">
                  <label className="flex min-h-8 items-start gap-2 text-xs font-normal leading-relaxed">
                    <input type="checkbox" className="mt-1 h-4 w-4" checked={editor.trackOpens} onChange={(event) => updateEditor('trackOpens', event.target.checked)} />
                    <span><strong className="font-medium">Traccia aperture</strong><br /><span className="text-muted-foreground">Registra le richieste del pixel; non dimostrano la lettura umana.</span></span>
                  </label>
                  <label className="flex min-h-8 items-start gap-2 text-xs font-normal leading-relaxed">
                    <input type="checkbox" className="mt-1 h-4 w-4" checked={editor.separateUntilReply} onChange={(event) => updateEditor('separateUntilReply', event.target.checked)} />
                    <span><strong className="font-medium">Separa fino alla risposta</strong><br /><span className="text-muted-foreground">Dopo l’invio mostra il messaggio in “Attesa risposta”; una risposta RFC reale lo sposta in “Risposte ricevute”.</span></span>
                  </label>
                </div>
                <div className="flex flex-col-reverse gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    {editingId && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button type="button" variant="outline" className="min-h-10 w-full rounded-none text-destructive sm:w-auto" disabled={archiveMutation.isPending}>
                            <Trash2 className="mr-2 h-4 w-4" />Archivia modello
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Archiviare questo modello?</AlertDialogTitle>
                            <AlertDialogDescription>Non sarà più selezionabile per nuovi invii. Le email già inviate e i loro snapshot non cambiano.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => archiveMutation.mutate(editingId)}>Archivia</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  <Button type="submit" className="min-h-10 rounded-none" disabled={!canSave || saveMutation.isPending}>
                    {saveMutation.isPending ? 'Salvataggio…' : editingId ? 'Salva modifiche' : 'Crea modello'}
                  </Button>
                </div>
              </form>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
